import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { theme } from 'assets/styles/theme';
import editIcon from 'assets/svg/edit-icon.svg';
import { useLocationPickerPopup } from 'components/location/picker/useLocationPickerPopup';
import { Text1 } from 'components/typography/Texts';
import { formatLocationLabel } from 'services/utils/location/locationPicker.utils';
import { LocationDetailsDto } from 'services/utils/location/locationService.dto';

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
  width: 100%;
  flex-wrap: wrap;
`;

const LocationLabel = styled(Text1)<{ $placeholder?: boolean }>`
  text-decoration: ${props => (props.$placeholder ? 'none' : 'underline')};
  color: ${props => (props.$placeholder ? theme.color.placeholderGrey : theme.color.black)};
`;

const EditButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: ${theme.color.red};
  font-size: 14px;
  padding: 0;
`;

const EditIcon = styled.img`
  width: 15px;
  height: 16px;
`;

interface Props {
  value?: LocationDetailsDto;
  onChange: (value?: LocationDetailsDto) => void;
  placeholder?: string;
  markerRadiusMiles?: number;
  mode?: 'popup' | 'step';
  onOpenStep?: () => void;
}

export const LocationField = ({
  value,
  onChange,
  placeholder,
  markerRadiusMiles,
  mode = 'popup',
  onOpenStep,
}: Props) => {
  const { t } = useTranslation();
  const { showLocationPicker } = useLocationPickerPopup();

  const label = formatLocationLabel(value) || placeholder || t('set-location');

  const openPicker = () => {
    if (mode === 'step' && onOpenStep) {
      onOpenStep();
      return;
    }

    showLocationPicker({
      defaultLocation: value,
      markerRadiusMiles,
      onConfirm: location => onChange(location),
    });
  };

  return (
    <Container data-testid={'location-field'}>
      <LocationLabel
        data-testid={'location-field-label'}
        $placeholder={!formatLocationLabel(value)}
        onClick={openPicker}>
        {label}
      </LocationLabel>
      <EditButton type='button' data-testid={'location-field-edit'} onClick={openPicker}>
        <EditIcon src={editIcon} alt='' />
        {t('edit')}
      </EditButton>
    </Container>
  );
};

export default LocationField;
